import BuildShowStore from "~/stores/BuildShowStore";
import { Build, Job, CommandJob, RetryableJob } from "~/components/build/Show/lib/types";
import {
  startTransition,
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { dropRight, filter, isNumber, matchesProperty, noop } from "lodash";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";
import { stepDependenciesQueryOptions } from "~/components/build/Show/lib/queries/stepDependencies";
import { useStepDependenciesStore } from "~/components/build/Show/lib/useStepDependenciesStore";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAllJobsQuery } from "~/components/build/Show/lib/queries/allJobs";
import { useStepsQuery } from "~/components/build/Show/lib/useSteps";
import { useBuildPreferencesStore } from "~/components/build/Show/lib/useBuildPreferencesStore";
import { stepQueryKey } from "~/components/build/Show/lib/queries/step";
import { jobDrawerQueryKey } from "~/components/build/Show/lib/queries/jobDrawer";
import useSyncStepsToStore from "~/components/build/Show/lib/useSyncStepsToStore";
import useSyncJobsToStore from "~/components/build/Show/lib/useSyncJobsToStore";

const BuildContext = createContext<NullableBuildContext>({
  build: null,
  store: null,
  setBuild: noop,
});

export const BuildContextProvider = ({
  children,
  store,
}: PropsWithChildren<{ store: BuildShowStore }>) => {
  const [build, setBuild] = useState(store.build);
  const setSteps = useBuildStore((state) => state.setSteps);
  const setJobs = useBuildStore((state) => state.setJobs);
  const queryClient = useQueryClient();

  /**
   * Fetch the step dependency graph, and store it in the useStepDependenciesStore.
   */
  const stepDependenciesQuery = useQuery(
    stepDependenciesQueryOptions(build, build.stepUploadsCount),
  );
  useEffect(() => {
    if (stepDependenciesQuery.data && Object.keys(stepDependenciesQuery.data).length > 0) {
      useStepDependenciesStore.getState().setDependenciesByStep(stepDependenciesQuery.data);
    }
  }, [stepDependenciesQuery.data]);

  // Get user preference for showing retried jobs, used in syncing and refetching
  const showRetriedJobs = useBuildPreferencesStore((state) => state.showRetriedJobs);

  // Watch top-level steps and jobs queries and sync data into the store.
  useSyncStepsToStore(queryClient, build);
  useSyncJobsToStore(queryClient, build, showRetriedJobs);

  /**
   * Fallback to referencing steps and jobs directly from the build.
   */
  useEffect(() => {
    if (!Features.ProgressiveBuildShow) {
      setSteps(build.steps, build);
      setJobs(build.jobs);
    }
  }, [build]);

  // Get query for fetching _all_ steps and _all_ jobs – used for refetching.
  // TODO: Remove once we're refetching only the steps/jobs that have changed
  const allStepsQuery = useStepsQuery(build, { enabled: false });
  const allJobsQuery = useAllJobsQuery(build, showRetriedJobs, { enabled: false });

  const handleStoreChange = useCallback(() => {
    setBuild(store.build);

    if (Features.ProgressiveBuildShow) {
      // TODO: Remove once we're refetching only the steps that have changed
      allStepsQuery.refetch().then((result) => {
        startTransition(() => {
          result.data?.forEach((step) => {
            // Invalidate the step queries to ensure we have the latest data
            queryClient.invalidateQueries({
              queryKey: stepQueryKey(build, step.uuid),
              exact: false, // We want to invalidate all queries that match the step
            });
          });
        });
      });

      // TODO: Revise once we're refetching only the jobs that have changed
      //
      // Skip refetching all the jobs if we're using the paginated jobs feature, since that's hinting there's most
      // likely a lot of jobs (ie. Snowflake), and we don't want to block the UI while fetching them all.
      if (!Features.BuildTablePaginatedJobs) {
        allJobsQuery.refetch().then((result) => {
          startTransition(() => {
            result.data?.records.forEach((job) => {
              queryClient.invalidateQueries({
                queryKey: jobDrawerQueryKey(build, job.id),
                exact: true,
              });
            });
          });
        });
      }
    }
  }, []);

  useEffect(() => {
    store.on("change", handleStoreChange);
    return () => {
      store.off("change", handleStoreChange);
    };
  }, []);

  const loadAndEmit = useCallback(
    (build: Build) => {
      store.loadAndEmit(build);
    },
    [store],
  );

  return (
    <BuildContext.Provider
      value={{
        build: build as Build & Record<string, unknown>,
        setBuild: loadAndEmit,
        store: store,
      }}
    >
      {children}
    </BuildContext.Provider>
  );
};

type StrictBuildContext = {
  build: Build;
  store: BuildShowStore;
  setBuild: (build: Build) => void;
};

type NullableBuildContext = {
  build: (Build & Record<string, unknown>) | null;
  store: BuildShowStore | null;
  setBuild: (build: Build) => void;
};

// Overload signatures
export function useBuild(): StrictBuildContext;
export function useBuild(options: { strict: false }): NullableBuildContext;

// Implementation
export function useBuild(options?: {
  strict?: boolean;
}): StrictBuildContext | NullableBuildContext {
  const context = useContext(BuildContext);

  if (context === undefined) {
    throw new Error("useBuild() must be used within a <BuildContextProvider />");
  }

  if (options?.strict !== false && (!context.build || !context.store)) {
    throw new Error("Build context is missing required build or store");
  }

  return context;
}

/**
 * Fetch the most recent parallel jobs for a given step.
 */
export const useMostRecentParallelJobsForStep = (stepUuid: string): CommandJob[] => {
  const jobs = useJobsForStep<CommandJob>(stepUuid);

  return mostRecentParallelJobs(jobs);
};

/**
 * Fetch the most recent matrix jobs for a given step.
 */
export const useMostRecentMatrixJobsForStep = (stepUuid: string): CommandJob[] => {
  const jobs = useJobsForStep<CommandJob>(stepUuid);

  return mostRecentMatrixJobs(jobs);
};

export function useJobsForStep<T extends Job>(stepUuid: string): T[] {
  const stepJobs = useBuildStore((state) => state.stepJobs);
  const { store } = useBuild({ strict: false });

  if (Features.ProgressiveBuildShow) {
    return (stepJobs.get(stepUuid) || []) as T[];
  }

  if (!store) {
    return [];
  }

  return (store.stepJobs.get(stepUuid) as T[]) || [];
}

export function useMostRecentJob<T extends Job>(stepId: string): T | null {
  const jobs = useJobsForStep<T>(stepId);
  if (!jobs.length) {
    return null;
  }
  return jobs[jobs.length - 1];
}

export function findJobsForStep<T extends Job>(build: Build, stepUuid: string): T[] {
  if (Features.ProgressiveBuildShow) {
    return (useBuildStore.getState().stepJobs.get(stepUuid) || []) as T[];
  }

  return build.jobs.filter((job) => job.stepUuid === stepUuid) as T[];
}

export function mostRecentParallelJobs(jobs: CommandJob[]): CommandJob[] {
  return Array.from(
    jobs
      .filter((job) => !job.retriedInJobUuid)
      .reduce((acc, job) => {
        acc.set(job.parallelGroupIndex, job);
        return acc;
      }, new Map())
      .values(),
  );
}

export function mostRecentMatrixJobs(jobs: CommandJob[]): CommandJob[] {
  return Array.from(
    jobs
      .filter((job) => !job.retriedInJobUuid)
      .reduce((acc, job) => {
        if (!job.matrix) {
          return acc;
        }

        acc.set(JSON.stringify(job.matrix), job);
        return acc;
      }, new Map())
      .values(),
  );
}

type UseRetriesForJobOptions = { includeLatest?: boolean };

export function useRetriesForJob(
  job: RetryableJob,
  { includeLatest = true }: UseRetriesForJobOptions = {},
): RetryableJob[] {
  let jobs = useJobsForStep<RetryableJob>(job.stepUuid);

  if ("matrix" in job && job.matrix) {
    jobs = filter(jobs, matchesProperty("matrix", job.matrix));
  }

  if ("parallelGroupIndex" in job && isNumber(job.parallelGroupIndex)) {
    jobs = filter(jobs, { parallelGroupIndex: job.parallelGroupIndex });
  }

  if (!includeLatest) {
    return dropRight(jobs, 1);
  }

  return jobs;
}

export default BuildContext;
